import copy from '../../copy/en/auth.json' with { type: 'json' };
import { formatCopy } from '../../copy/format.js';
import { Outlet, useLocation, useNavigate } from "react-router";
import { Button } from "../components/ui/Button.js";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useSession } from "./SessionProvider.js";
import "./viewAs.css";

/** Outside /me's error boundary so an unavailable target can always be exited. */
export function ViewAsBoundary() {
  const { viewAs, endViewAs, mfaChallengeRequested, viewAsNavigation, finishViewAsNavigation } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!viewAsNavigation) return;
    // Passive cleanup unregisters the previous profile's pending route blocker
    // before this new router subscriber performs the navigation handoff.
    if (`${location.pathname}${location.search}` === viewAsNavigation) finishViewAsNavigation?.();
    else void navigate(viewAsNavigation, { replace: true });
  }, [viewAsNavigation, navigate, finishViewAsNavigation, location.pathname, location.search]);
  const banner = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = banner.current;
    const update = () => document.documentElement.style.setProperty("--view-as-height", `${element?.getBoundingClientRect().height ?? 0}px`);
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    if (element) observer?.observe(element);
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); document.documentElement.style.removeProperty("--view-as-height"); };
  }, [viewAs]);
  const securityRoute = /^\/(?:mfa|join|activate|forgot-password|reset-password)(?:\/|$)/.test(location.pathname);
  return <>
    {viewAs ? <div ref={banner} className="view-as-banner" role="region" aria-label={copy.ViewAsBoundary.portalPreview}>
      <div><strong>{formatCopy(copy.ViewAsBoundary.viewingPortalAs, { name: viewAs.displayName })}</strong><span>{copy.ViewAsBoundary.readOnlyPreviewExplicitPublicLinkCreation}</span></div>
      <Button variant="secondary" onClick={() => { endViewAs?.(); }}>{copy.ViewAsBoundary.returnToAdministrator}</Button>
    </div> : null}
    {viewAsNavigation ? <p role="status">{copy.ViewAsBoundary.changingPortalView}</p> : viewAs && (securityRoute || mfaChallengeRequested) ? <main className="main"><p role="status">{mfaChallengeRequested ? copy.ViewAsBoundary.yourAdministratorSessionNeedsTwoFactorVerification : copy.ViewAsBoundary.accountSecurityAndOnboardingAreUnavailableDuring}</p></main> : <Outlet />}
  </>;
}
